import type { IPlaybackAspectTelemetrySample, TVerticalAspectSignal } from '@lucimark/shared/helpers/playbackAspectTelemetry'
import {
  isLandscapeScreenAspectClass,
  verticalAspectSignals
} from '@lucimark/shared/helpers/playbackAspectTelemetry'
import * as Sentry from '@sentry/vue'
import { setPlayerDiagnosticFaultActive } from '@/helpers/playerDiagnostics/playerDiagnosticService'
import { isPlayerDiagnosticsSamplingEnabled } from '@/helpers/playerDiagnostics/playerDiagnosticsKillSwitch'

/** Last sample observed (for diagnostic snapshot fields when sampling is on). */
let lastSample: IPlaybackAspectTelemetrySample | null = null

/** Signals that currently have an open Sentry episode (rising-edge dedupe). */
const activeVerticalSignals = new Set<TVerticalAspectSignal>()

/** Test-only. */
export function resetPlaybackAspectTelemetryObserverForTests(): void {
  lastSample = null
  activeVerticalSignals.clear()
}

export function peekLastPlaybackAspectTelemetrySample(): IPlaybackAspectTelemetrySample | null {
  return lastSample
}

/**
 * Merge a new cold-path sample with the previous one so partial samples (viewport-only /
 * heartbeat without layout) do not wipe on-air / media fields that were just measured.
 */
function mergePlaybackAspectSample(
  previous: IPlaybackAspectTelemetrySample | null,
  next: IPlaybackAspectTelemetrySample
): IPlaybackAspectTelemetrySample {
  if (previous == null) {
    return next
  }
  const merged: IPlaybackAspectTelemetrySample = {
    ...previous,
    ...next
  }
  // Omitted optional aspects must keep the prior value (spread already does this when
  // `next` lacks the key; re-assert so undefined explicit clears cannot sneak in).
  if (next.onAirFrameLayoutAspect == null && previous.onAirFrameLayoutAspect != null) {
    merged.onAirFrameId = previous.onAirFrameId
    merged.onAirFrameLayoutWidthPx = previous.onAirFrameLayoutWidthPx
    merged.onAirFrameLayoutHeightPx = previous.onAirFrameLayoutHeightPx
    merged.onAirFrameLayoutAspect = previous.onAirFrameLayoutAspect
  }
  if (next.visibleMediaAspect == null && previous.visibleMediaAspect != null) {
    merged.visibleMediaWidthPx = previous.visibleMediaWidthPx
    merged.visibleMediaHeightPx = previous.visibleMediaHeightPx
    merged.visibleMediaAspect = previous.visibleMediaAspect
  }
  return merged
}

/**
 * Clear a signal only when this sample *explicitly* measures that channel as non-vertical.
 * Partial samples that omit layout/media must not close open episodes (Bugbot #1529).
 */
function applyVerticalSignalEdges(
  sample: IPlaybackAspectTelemetrySample
): { rising: TVerticalAspectSignal[], activeKey: string } {
  // Portrait (and unknown) panels are supposed to be vertical. Drop any open
  // episode so a landscape false-positive does not stick after rotation.
  if (!isLandscapeScreenAspectClass(sample.screenAspectClass)) {
    activeVerticalSignals.clear()
    return { rising: [], activeKey: '' }
  }

  const measured = verticalAspectSignals(sample)
  const rising: TVerticalAspectSignal[] = []

  // Viewport is always present on every sample.
  if (measured.includes('viewport')) {
    if (!activeVerticalSignals.has('viewport')) {
      activeVerticalSignals.add('viewport')
      rising.push('viewport')
    }
  }
  else {
    activeVerticalSignals.delete('viewport')
  }

  if (sample.onAirFrameLayoutAspect != null) {
    if (measured.includes('on_air_frame_layout')) {
      if (!activeVerticalSignals.has('on_air_frame_layout')) {
        activeVerticalSignals.add('on_air_frame_layout')
        rising.push('on_air_frame_layout')
      }
    }
    else {
      activeVerticalSignals.delete('on_air_frame_layout')
    }
  }

  if (sample.visibleMediaAspect != null) {
    if (measured.includes('visible_media_rect')) {
      if (!activeVerticalSignals.has('visible_media_rect')) {
        activeVerticalSignals.add('visible_media_rect')
        rising.push('visible_media_rect')
      }
    }
    else {
      activeVerticalSignals.delete('visible_media_rect')
    }
  }

  const activeKey = [...activeVerticalSignals].sort().join(',')
  return { rising, activeKey }
}

/**
 * Observe a playback aspect sample (viewport / on-air layout / painted media).
 *
 * Always-on for Sentry rising edges. Must be called from cold paths only
 * (SIZE/ROTATION sync, frame transition, heartbeat) — never per RAF tick with DOM.
 * Failures are swallowed (playback-safety).
 */
export function observePlaybackAspectTelemetry(
  sample: IPlaybackAspectTelemetrySample,
  opts?: { playerId?: string | null, tenantId?: string | null }
): void {
  try {
    const merged = mergePlaybackAspectSample(lastSample, sample)
    lastSample = merged
    const { rising, activeKey } = applyVerticalSignalEdges(sample)

    if (rising.length > 0) {
      reportVerticalAspectPlaybackToSentry(merged, rising, activeKey, opts)
    }

    if (isPlayerDiagnosticsSamplingEnabled(sample.collectedAtMs)) {
      setPlayerDiagnosticFaultActive(
        'vertical_aspect_playback',
        activeVerticalSignals.size > 0,
        sample.collectedAtMs,
        0
      )
    }
  }
  catch {
    /* never break playback */
  }
}

function reportVerticalAspectPlaybackToSentry(
  sample: IPlaybackAspectTelemetrySample,
  rising: string[],
  signalKey: string,
  opts?: { playerId?: string | null, tenantId?: string | null }
): void {
  try {
    Sentry.captureMessage('player_vertical_aspect_playback', {
      level: 'warning',
      tags: {
        vertical_aspect_signals: signalKey || 'none',
        vertical_aspect_rising: rising.join(','),
        viewport_aspect: sample.viewportAspect,
        on_air_layout_aspect: sample.onAirFrameLayoutAspect ?? 'unknown',
        visible_media_aspect: sample.visibleMediaAspect ?? 'unknown',
        screen_rotation: sample.screenRotation,
        aspect_restrict_active: sample.aspectRestrictActive ? 'true' : 'false',
        screen_aspect_class: sample.screenAspectClass
      },
      contexts: {
        playback_aspect: {
          ...sample,
          playerId: opts?.playerId ?? undefined,
          tenantId: opts?.tenantId ?? undefined
        }
      },
      fingerprint: ['player_vertical_aspect_playback', signalKey || 'none']
    })
  }
  catch {
    /* Sentry unavailable */
  }
}
